import React, { useCallback, useEffect, useMemo, useState } from "react";
import SummaryCards from "../../components/Common/SummaryCards";
import { showLoading } from "react-global-loading";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import ActionMenu from "../../components/Common/ActionMenu";
import useAdminDataLifecycle from "../../components/Common/useAdminDataLifecycle";
import { getReportTemplates } from "../../helpers/backend_helper";
import { ErrorNotify } from "../../shared/Alert";
import "../Standards/Standards.scss";

const ReportTemplateActions = ({ template, onEdit, lifecycleActions }) => (
  <ActionMenu
    label={template.name}
    actions={[
      {
        label: "Edit",
        icon: <i className="bx bx-pencil" />,
        onSelect: () => onEdit(template.guid),
      },
      ...lifecycleActions,
    ]}
  />
);

const ReportTemplateList = ({ onEdit, refreshRequest = 0 }) => {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const loadData = useCallback(() => {
    showLoading(true);
    return getReportTemplates()
      .then((response) => {
        if (response.status) setTemplates(response.data || []);
        else
          ErrorNotify(response.message || "Unable to load report templates.");
      })
      .catch(() => ErrorNotify("Unable to load report templates."))
      .finally(() => showLoading(false));
  }, []);
  const lifecycle = useAdminDataLifecycle(loadData);
  useEffect(() => {
    loadData();
  }, [loadData, refreshRequest]);
  const filtered = useMemo(
    () =>
      templates.filter(
        (x) =>
          (status === "all" || x.status === status) &&
          `${x.name || ""} ${x.description || ""} ${x.reportTypeName || ""}`
            .toLowerCase()
            .includes(search.toLowerCase())
      ),
    [templates, search, status]
  );
  const columns = [
    {
      id: "name",
      header: "Template",
      accessorKey: "name",
      minSize: 230,
      flex: 1,
      cell: ({ row, getValue }) => (
        <button
          type="button"
          className="standards-grid__name"
          onClick={() => onEdit(row.original.guid)}
        >
          <span>{(getValue() || "T").charAt(0)}</span>
          {getValue()}
        </button>
      ),
    },
    {
      id: "reportType",
      header: "Report Type",
      accessorKey: "reportTypeName",
      minSize: 150,
      flex: 0.7,
    },
    {
      id: "version",
      header: "Version",
      accessorKey: "versionNo",
      minSize: 90,
      flex: 0.4,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      minSize: 110,
      flex: 0.5,
    },
    {
      id: "updated",
      header: "Last Updated",
      accessorKey: "updatedAt",
      minSize: 170,
      flex: 0.7,
      cell: ({ getValue }) =>
        getValue() ? new Date(getValue()).toLocaleString() : "",
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      minSize: 72,
      flex: 0,
      cell: ({ row }) => (
        <ReportTemplateActions
          template={row.original}
          onEdit={onEdit}
          lifecycleActions={lifecycle.actions("report-template", row.original)}
        />
      ),
    },
  ];
  return (
    <section className="standards-page__content">
      <SummaryCards
        items={[
          [
            "bx-file",
            "blue",
            "Total Report Templates",
            templates.length,
            "Templates in your firm",
          ],
          [
            "bx-check-circle",
            "green",
            "Published",
            templates.filter((x) => x.status === "Published").length,
            "Available for reuse",
          ],
          [
            "bx-edit",
            "orange",
            "Drafts",
            templates.filter((x) => x.status === "Draft").length,
            "Work in progress",
          ],
        ]}
        ariaLabel="Report template summary"
      />
      <section className="standards-grid-panel">
        
        <CustomDataGrid
 filterContent={<section className="standards-filter-panel">
          <div className="standards-filter-panel__fields">
            <label>
              <span>Search</span>
              <div className="standards-filter-panel__search">
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search report templates..."
                />
              </div>
            </label>
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="all">All statuses</option>
                <option>Draft</option>
                <option>Published</option>
                <option>Archived</option>
              </select>
            </label>
          </div>
          <div className="standards-filter-panel__actions">
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatus("all");
              }}
            >Clear</button>
          </div>
        </section>}

          data={filtered}
          columns={columns}
          fileName="report-templates"
          recordLabel="report templates"
          showToolbar={false}
          initialPageSize={15}
          rowHeight={56}
          columnHeaderHeight={56}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e7ebf2",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              { backgroundColor: "#ffffff !important", color: "#52627f" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".2px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              fontSize: 13,
              borderBottomColor: "#edf0f5",
              color: "#273957",
            },
            "& .MuiDataGrid-row": { minHeight: "56px !important" },
            "& .MuiDataGrid-footerContainer, & .MuiTypography-root": {
              fontSize: 14,
              borderTopColor: "#e7ebf2",
            },
          }}
        />
      </section>
      {lifecycle.dialog}
    </section>
  );
};
export default ReportTemplateList;
